import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { authApi } from "api/auth/api";
import cn from "classnames";
import { Button } from "components/ui/Button/Button";
import { FormField } from "components/ui/FormField";
import { ChineseBrothersLogoIcon, QubictronLogoIcon } from "components/ui/Icons";
import { Link } from "components/ui/Link";
import { Loader } from "components/ui/Loader/Loader";
import { SocialLogin } from "components/ui/SocialLogin/SocialLogin";
import { SIGN_UP_ROUTE, TERMS_AND_CONDITIONS_ROUTE } from "constants/routesValues";
import { SITE_NAME } from "constants/siteName";
import { VALIDATION_SCHEMA } from "constants/validationSchema";
import { useSiteName } from "context/SiteName.context";
import { createRouteAction } from "utils/history.utils";
import { getRecaptchaToken } from "utils/recaptcha";
import { STATIC_IMAGES } from "utils/url.utils";
import style from "./SignIn.module.scss";

type SignInFormValues = {
  email: string;
  password: string;
};

export const SignIn = () => {
  const { siteName } = useSiteName();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isSignInFetching, setIsSignInFetching] = useState<boolean>(false);
  const formMethods = useForm<SignInFormValues>({
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onSubmit",
  });

  const onSubmit = async (_data: SignInFormValues) => {
    setIsSignInFetching(true);
    setErrorMessage("");

    let recaptcha: string;
    try {
      recaptcha = await getRecaptchaToken("SIGNIN");
    } catch {
      setIsSignInFetching(false);
      setErrorMessage("Error with reCAPTCHA verification.");
      return;
    }

    await authApi
      .signIn({
        email: _data.email,
        password: _data.password,
        recaptcha,
      })
      .then((response) => {
        if (response.status === 204) {
          const userResponse = authApi.getUser();
          userResponse
            .then((response) => {
              setIsSignInFetching(false);
              localStorage.setItem("user", JSON.stringify(response.data));
              window.location.href = "/";
            })
            .catch((error) => {
              throw error;
            });
        }
      })
      .catch((error) => {
        setIsSignInFetching(false);
        const status = error.response?.status;
        const errors = error.response?.data?.errors;

        if (status === 422 && errors?.recaptcha) {
          setErrorMessage(errors.recaptcha[0] ?? "reCAPTCHA verification failed.");
          return;
        }

        if (status === 500) {
          setErrorMessage("Server error. Please try again later.");
          return;
        }

        setErrorMessage(error.response?.data?.message || "Something went wrong.");
      });
  };

  const isChineseBrothers = siteName === SITE_NAME.CHINESE_BROTHERS;

  return (
    <div
      className={cn(style.signInWrapper, {
        [style.signInWrapperChineseBrothers]: isChineseBrothers,
      })}
    >
      <div className={style.signInContent}>
        <h3 className={style.signInTitle}>
          Welcome to <br className={style.signInTitleBreak} /> {isChineseBrothers ? "Chinese Brothers!" : "Qubictron!"}
        </h3>
        <FormProvider {...formMethods}>
          <form id="signInForm" className={style.form} onSubmit={formMethods.handleSubmit(onSubmit)} method="POST">
            <Controller
              name="email"
              control={formMethods.control}
              rules={VALIDATION_SCHEMA.email}
              render={({ field, fieldState }) => (
                <FormField
                  name={field.name}
                  labelTitle="Email"
                  type="email"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error}
                />
              )}
            />
            <Controller
              name="password"
              control={formMethods.control}
              rules={VALIDATION_SCHEMA.password}
              render={({ field, fieldState }) => (
                <FormField
                  name={field.name}
                  labelTitle="Password"
                  type="password"
                  isPassword
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error}
                />
              )}
            />
            <span className={style.formDescription}>
              By signing in or creating an account, you agree with our{" "}
              <Link
                className={style.formDescriptionTermsLink}
                href={TERMS_AND_CONDITIONS_ROUTE}
                text="Terms of Service"
                target="_blank"
              />
            </span>
            <Button
              disabled={isSignInFetching}
              className={cn(style.formButton, {
                [style.formButtonChineseBrothers]: isChineseBrothers,
              })}
              text={isSignInFetching ? "Please wait" : "Sign In"}
              type="submit"
            >
              {isSignInFetching && <Loader className={style.loader} />}
            </Button>
            {errorMessage && <p className={style.errorMessage}>{errorMessage}</p>}
          </form>
        </FormProvider>
        <span className={style.continueWith}>Or continue with</span>
        <SocialLogin />
        <p className={style.formDescriptionLink}>
          Don’t have an account? <span onClick={() => createRouteAction(SIGN_UP_ROUTE)}>Sign up</span>
        </p>
      </div>
      <div
        className={cn(style.signInBackgroundImage, {
          [style.signInBackgroundImageChineseBrothers]: isChineseBrothers,
        })}
      >
        {isChineseBrothers ? (
          <>
            <div className={style.chineseBrothersGlowTop} aria-hidden={true} />
            <div className={style.chineseBrothersGlowBottom} aria-hidden={true} />
            <ChineseBrothersLogoIcon className={style.chineseBrothersLogo} />
          </>
        ) : (
          <>
            <QubictronLogoIcon className={style.logoIcon} />
            <img src={`${STATIC_IMAGES}/SignIn/mainBackgroundImage.webp`} alt="" />
          </>
        )}
      </div>
    </div>
  );
};
